import { Link } from 'react-router-dom';
import { Bike, Clock, MapPin, ShieldCheck, Wrench } from 'lucide-react';
import { Button } from '~/components/ui/button';
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/components/ui/primitives';
import { LoadingState } from '~/components/shared/states';
import { useCompany, usePackages, useServiceTypes, useZones } from '~/lib/queries';
import { formatDuration, formatPrice } from '~/lib/utils';

export function HomePage() {
  const { data: company } = useCompany();
  const { data: serviceTypes, isLoading: loadingServices } = useServiceTypes();
  const { data: packages } = usePackages();
  const { data: zones } = useZones();

  return (
    <div className="flex flex-col">
      {/* --- Banniere ---------------------------------------------------------- */}
      <section className="border-b border-border bg-muted/30">
        <div className="mx-auto grid w-full max-w-6xl gap-8 px-4 py-12 sm:py-16 lg:grid-cols-2 lg:items-center">
          <div className="space-y-5">
            <Badge variant="secondary" className="gap-1.5">
              <MapPin className="size-3" aria-hidden />
              {zones?.length ?? 0} secteurs couverts sur la metropole lyonnaise
            </Badge>

            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
              L'atelier vient chez vous.
            </h1>

            <p className="text-base text-muted-foreground">
              {company?.aboutText ??
                'Reparation et entretien de velos et VAE a domicile. Nos techniciens interviennent chez vous, avec le meme niveau d’exigence qu’en boutique.'}
            </p>

            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg">
                <Link to="/reserver">
                  <Bike aria-hidden />
                  Prendre rendez-vous
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/connexion">J'ai deja un compte</Link>
              </Button>
            </div>

            <ul className="grid gap-2 pt-2 text-sm text-muted-foreground sm:grid-cols-3">
              <li className="flex items-center gap-2">
                <Clock className="size-4 shrink-0" aria-hidden />
                Creneaux garantis
              </li>
              <li className="flex items-center gap-2">
                <ShieldCheck className="size-4 shrink-0" aria-hidden />
                Techniciens salaries
              </li>
              <li className="flex items-center gap-2">
                <Wrench className="size-4 shrink-0" aria-hidden />
                Pieces en stock
              </li>
            </ul>
          </div>

          <Card className="lg:justify-self-end lg:w-full lg:max-w-md">
            <CardHeader>
              <CardTitle>Comment ca marche</CardTitle>
              <CardDescription>Quatre etapes, moins de deux minutes.</CardDescription>
            </CardHeader>
            <CardContent>
              <ol className="flex flex-col gap-4">
                {[
                  ['Votre adresse', 'Nous verifions immediatement que votre secteur est couvert.'],
                  ['Votre velo', 'Marque, modele, annee et type : musculaire ou electrique.'],
                  ['Votre forfait', 'Un tarif clair, une duree annoncee, aucune surprise.'],
                  [
                    'Votre creneau',
                    'Vous choisissez. Le compte se cree apres, si vous le souhaitez.',
                  ],
                ].map(([title, description], index) => (
                  <li key={title} className="flex gap-3">
                    <span
                      className="bg-primary text-primary-foreground flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold"
                      aria-hidden
                    >
                      {index + 1}
                    </span>
                    <div className="space-y-0.5">
                      <p className="font-medium">{title}</p>
                      <p className="text-sm text-muted-foreground">{description}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* --- Prestations -------------------------------------------------------- */}
      <section className="mx-auto w-full max-w-6xl px-4 py-12">
        <div className="mb-6 space-y-1">
          <h2 className="text-xl font-semibold tracking-tight">Nos prestations</h2>
          <p className="text-sm text-muted-foreground">
            Des forfaits a duree et prix fixes, etablis a l'avance.
          </p>
        </div>

        {loadingServices ? (
          <LoadingState rows={2} />
        ) : (
          <div className="grid gap-4 md:grid-cols-3">
            {serviceTypes?.map((service) => {
              const servicePackages =
                packages?.filter((item) => item.serviceType?.id === service.id) ?? [];

              return (
                <Card key={service.id}>
                  <CardHeader>
                    <CardTitle>{service.name}</CardTitle>
                    <CardDescription>{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-col gap-2">
                    {servicePackages.map((item) => (
                      <div
                        key={item.id}
                        className="app-summary-box app-summary-row flex-wrap gap-1"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium">{item.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDuration(item.durationMin)}
                            {item.cycleType === 'EBIKE' ? ' · VAE uniquement' : null}
                          </p>
                        </div>
                        <span className="text-sm font-semibold tabular-nums">
                          {formatPrice(item.price)}
                        </span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* --- Zones ------------------------------------------------------------- */}
      {zones && zones.length > 0 ? (
        <section className="border-t border-border bg-muted/30">
          <div className="mx-auto w-full max-w-6xl px-4 py-10">
            <h2 className="mb-4 text-xl font-semibold tracking-tight">Secteurs desservis</h2>
            <ul className="flex flex-wrap gap-2">
              {zones.map((zone) => (
                <li key={zone.id}>
                  <Badge variant="outline" className="gap-1.5 py-1">
                    <span
                      className="size-2 rounded-full"
                      style={{ backgroundColor: zone.color }}
                      aria-hidden
                    />
                    {zone.name}
                  </Badge>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm text-muted-foreground">
              Votre adresse n'apparait pas ? Indiquez-la lors de la reservation : nous vous
              previendrons des l'ouverture de votre secteur.
            </p>
          </div>
        </section>
      ) : null}
    </div>
  );
}
